import React from 'react';
import { ArrowLeft, Shield, Eye, AlertTriangle, CheckCircle, Users, Lock, UserCheck, Ban, FileText, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import MarketingShell from './MarketingShell';

interface TrustCenterProps {
    onBack?: () => void;
}

const TrustCenter: React.FC<TrustCenterProps> = ({ onBack }) => {
    const { t } = useTranslation('legal');

    const isItems = t('trust_center.what_is_replica.is_items', { returnObjects: true }) as string[];
    const isNotItems = t('trust_center.what_is_replica.is_not_items', { returnObjects: true }) as string[];
    const logItems = t('trust_center.auditability.log_items', { returnObjects: true }) as string[];
    const actionItems = t('trust_center.auditability.action_items', { returnObjects: true }) as string[];
    const comparisonRows = t('trust_center.comparison.rows', { returnObjects: true }) as Array<{ autonomous: string; humanreplica: string }>;
    const neverDoItems = t('trust_center.never_do.items', { returnObjects: true }) as string[];

    return (
        <MarketingShell>
            {/* Page header */}
            <div className="border-b border-white/[0.06] bg-white/[0.02]">
                <div className="max-w-4xl mx-auto px-6 pt-10 pb-10">
                    {onBack && (
                        <button
                            onClick={onBack}
                            className="inline-flex items-center gap-2 text-white/40 hover:text-white/70 text-sm mb-8 transition-colors"
                        >
                            <ArrowLeft size={15} />
                            Back
                        </button>
                    )}
                    <div className="flex items-center gap-4">
                        <div className="w-11 h-11 rounded-xl bg-[#0A84FF]/10 flex items-center justify-center flex-shrink-0">
                            <Shield size={22} className="text-[#0A84FF]" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-white">{t('trust_center.title')}</h1>
                            <p className="text-white/50 text-sm mt-0.5">{t('trust_center.subtitle')}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-4xl mx-auto px-6 py-12">
                <div className="space-y-6">

                    {/* 1. Who Is In Control */}
                    <section className="bg-[#0A84FF]/[0.07] border border-[#0A84FF]/20 rounded-2xl p-8">
                        <div className="flex items-center gap-3 mb-4">
                            <UserCheck size={26} className="text-[#0A84FF]" />
                            <h2 className="text-xl font-bold text-white">{t('trust_center.control.title')}</h2>
                        </div>
                        <p className="text-lg text-white font-medium mb-3">{t('trust_center.control.headline')}</p>
                        <p className="text-white/60 leading-relaxed">{t('trust_center.control.description')}</p>
                    </section>

                    {/* 2. Legal Documents */}
                    <section className="bg-white/[0.04] backdrop-blur-sm border border-white/[0.06] rounded-2xl p-6">
                        <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                            <FileText size={20} className="text-[#0A84FF]" />
                            {t('trust_center.legal_docs_title', 'Legal Documents')}
                        </h2>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <a
                                href="/privacy"
                                className="flex items-center justify-between p-4 rounded-xl border border-white/[0.08] bg-white/[0.04] hover:bg-white/[0.08] transition-colors group"
                            >
                                <div>
                                    <p className="font-semibold text-white group-hover:text-[#0A84FF] transition-colors">
                                        {t('trust_center.privacy_link', 'Privacy Policy')}
                                    </p>
                                    <p className="text-sm text-white/40 mt-1">
                                        {t('trust_center.privacy_link_desc', 'How we handle and protect your data')}
                                    </p>
                                </div>
                                <ExternalLink size={16} className="text-white/30 group-hover:text-[#0A84FF] flex-shrink-0 ml-3" />
                            </a>

                        </div>
                    </section>

                    {/* 3. HumanReplica vs Autonomous AI */}
                    <section className="bg-white/[0.04] backdrop-blur-sm border border-white/[0.06] rounded-2xl p-6">
                        <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                            <Ban size={20} className="text-[#0A84FF]" />
                            {t('trust_center.comparison.title')}
                        </h2>
                        <div className="overflow-x-auto mb-6 rounded-xl border border-white/[0.06]">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-white/[0.08]">
                                        <th className="text-left py-3 px-4 font-semibold text-white/70 bg-red-500/[0.06]">
                                            {t('trust_center.comparison.autonomous_header')}
                                        </th>
                                        <th className="text-left py-3 px-4 font-semibold text-white/70 bg-emerald-500/[0.06]">
                                            {t('trust_center.comparison.humanreplica_header')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="text-white/55">
                                    {Array.isArray(comparisonRows) && comparisonRows.map((row, idx) => (
                                        <tr key={idx} className={idx < comparisonRows.length - 1 ? 'border-b border-white/[0.04]' : ''}>
                                            <td className="py-3 px-4 bg-red-500/[0.03]">{row.autonomous}</td>
                                            <td className="py-3 px-4 bg-emerald-500/[0.03]">{row.humanreplica}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="bg-red-500/[0.07] border border-red-500/20 rounded-xl p-5">
                            <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                                <Ban size={16} className="text-red-400" />
                                {t('trust_center.never_do.title')}
                            </h3>
                            <ul className="text-white/60 space-y-2 text-sm">
                                {Array.isArray(neverDoItems) && neverDoItems.map((item, idx) => (
                                    <li key={idx}>• {item}</li>
                                ))}
                            </ul>
                        </div>
                    </section>

                    {/* 4. Auditability */}
                    <section className="bg-white/[0.04] backdrop-blur-sm border border-white/[0.06] rounded-2xl p-6">
                        <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                            <Eye size={20} className="text-[#0A84FF]" />
                            {t('trust_center.auditability.title')}
                        </h2>
                        <p className="text-white/60 mb-6">{t('trust_center.auditability.simplified_description')}</p>
                        <div className="grid md:grid-cols-2 gap-4">
                            <div className="bg-white/[0.06] rounded-xl p-4">
                                <h4 className="font-medium text-white mb-2">{t('trust_center.auditability.what_we_log')}</h4>
                                <ul className="text-sm text-white/60 space-y-1">
                                    {Array.isArray(logItems) && logItems.map((item, idx) => (
                                        <li key={idx}>• {item}</li>
                                    ))}
                                </ul>
                            </div>
                            <div className="bg-white/[0.06] rounded-xl p-4">
                                <h4 className="font-medium text-white mb-2">{t('trust_center.auditability.what_you_can_do')}</h4>
                                <ul className="text-sm text-white/60 space-y-1">
                                    {Array.isArray(actionItems) && actionItems.map((item, idx) => (
                                        <li key={idx}>• {item}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                        <div className="mt-6 p-4 bg-[#0A84FF]/[0.07] border border-[#0A84FF]/20 rounded-xl">
                            <p className="text-sm text-white/60">
                                <strong className="text-[#0A84FF]">{t('trust_center.auditability.attribution_note')}</strong>{' '}
                                {t('trust_center.auditability.attribution_desc')}
                            </p>
                        </div>
                    </section>

                    {/* 5. What Is a Replica */}
                    <section className="bg-white/[0.04] backdrop-blur-sm border border-white/[0.06] rounded-2xl p-6">
                        <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                            <Users size={20} className="text-[#0A84FF]" />
                            {t('trust_center.what_is_replica.title')}
                        </h2>
                        <div className="grid md:grid-cols-2 gap-6">
                            <div className="bg-white/[0.06] rounded-xl p-5">
                                <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                                    <CheckCircle size={18} className="text-emerald-400" />
                                    {t('trust_center.what_is_replica.is_title')}
                                </h3>
                                <ul className="text-white/60 space-y-2 text-sm">
                                    {Array.isArray(isItems) && isItems.map((item, idx) => (
                                        <li key={idx}>• {item}</li>
                                    ))}
                                </ul>
                            </div>
                            <div className="bg-white/[0.06] rounded-xl p-5">
                                <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                                    <AlertTriangle size={18} className="text-amber-400" />
                                    {t('trust_center.what_is_replica.is_not_title')}
                                </h3>
                                <ul className="text-white/60 space-y-2 text-sm">
                                    {Array.isArray(isNotItems) && isNotItems.map((item, idx) => (
                                        <li key={idx}>• {item}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </section>

                    {/* 6. Security */}
                    <section className="bg-white/[0.04] backdrop-blur-sm border border-white/[0.06] rounded-2xl p-6">
                        <h2 className="text-base font-semibold text-white/60 mb-4 flex items-center gap-2">
                            <Lock size={16} className="text-white/30" />
                            {t('trust_center.security.title')}
                        </h2>
                        <p className="text-sm text-white/45 mb-4">{t('trust_center.security.description')}</p>
                        <div className="flex flex-wrap gap-3">
                            <span className="inline-flex items-center px-4 py-1.5 bg-white/[0.06] text-white/55 text-xs rounded-full border border-white/[0.08] font-medium tracking-wide">
                                {t('trust_center.security.soc2')}
                            </span>
                            <span className="inline-flex items-center px-4 py-1.5 bg-white/[0.06] text-white/55 text-xs rounded-full border border-white/[0.08] font-medium tracking-wide">
                                {t('trust_center.security.gdpr')}
                            </span>
                            <span className="inline-flex items-center px-4 py-1.5 bg-white/[0.06] text-white/55 text-xs rounded-full border border-white/[0.08] font-medium tracking-wide">
                                {t('trust_center.security.iso')}
                            </span>
                        </div>
                    </section>

                    {/* 7. Contact */}
                    <section className="bg-white/[0.04] backdrop-blur-sm border border-white/[0.06] rounded-2xl p-6">
                        <h2 className="text-lg font-semibold text-white mb-4">{t('trust_center.questions.title')}</h2>
                        <p className="text-white/60 mb-6">{t('trust_center.questions.description')}</p>
                        <div className="flex gap-3 flex-wrap">
                            <a
                                href="mailto:contact@humanreplica.ai"
                                className="inline-flex items-center px-5 py-2.5 rounded-xl bg-[#0A84FF] text-white text-sm font-semibold hover:bg-[#0076E6] transition-colors"
                            >
                                {t('trust_center.questions.contact_trust')}
                            </a>
                            <a
                                href="/privacy"
                                className="inline-flex items-center px-5 py-2.5 rounded-xl bg-white/[0.07] border border-white/[0.10] text-white/75 text-sm font-semibold hover:bg-white/[0.12] transition-colors"
                            >
                                {t('trust_center.questions.privacy_policy')}
                            </a>
                        </div>
                    </section>

                </div>
            </div>
        </MarketingShell>
    );
};

export default TrustCenter;
